import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tag, Plus, Pencil, Trash2, Loader2, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

export default function BrandsPage() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBrand, setEditBrand] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/catalog/brands').then(r => setBrands(r.data)).catch(() => toast.error('Failed to load brands')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const slug = form.slug || slugify(form.name);
      await api.post('/admin/catalog/brands', { ...form, slug });
      toast.success('Brand created');
      setShowCreate(false);
      setForm({ name: '', slug: '', sort_order: 0 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!editBrand) return;
    setSaving(true);
    try {
      await api.put(`/admin/catalog/brands/${editBrand._origSlug}`, form);
      toast.success('Brand updated');
      setEditBrand(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (brand) => {
    try {
      await api.put(`/admin/catalog/brands/${brand.slug}`, { is_active: !brand.is_active });
      toast.success(brand.is_active ? 'Deactivated' : 'Reactivated');
      load();
    } catch (err) { toast.error('Failed'); }
  };

  const handleDelete = async (brand) => {
    if (!confirm(`Delete "${brand.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/catalog/brands/${brand.slug}`);
      toast.success('Brand deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot delete');
    }
  };

  const openEdit = (b) => {
    setForm({ name: b.name, slug: b.slug, sort_order: b.sort_order || 0, is_active: b.is_active });
    setEditBrand({ ...b, _origSlug: b.slug });
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="brands-admin-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-ink" />
          <h1 className="text-base font-semibold text-ink">Brand Management</h1>
          <Badge variant="outline" className="text-[10px]">{brands.length} brands</Badge>
        </div>
        <Button size="sm" className="gap-1" onClick={() => { setForm({ name: '', slug: '', sort_order: brands.length }); setShowCreate(true); }} data-testid="create-brand-btn">
          <Plus className="h-3 w-3" /> Add Brand
        </Button>
      </div>

      <Card className="bg-white border-stone/50">
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-stone/30">
                <th className="text-left p-3 font-medium text-muted-foreground"><ArrowUpDown className="h-3 w-3 inline mr-1" />Order</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Name</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Slug</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Refs</th>
                <th className="text-right p-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.map(b => (
                <tr key={b.id} className="border-b border-stone/10 hover:bg-stone/5">
                  <td className="p-3 text-muted-foreground font-mono">{b.sort_order}</td>
                  <td className="p-3 font-medium text-ink">{b.name}</td>
                  <td className="p-3 font-mono text-muted-foreground">{b.slug}</td>
                  <td className="p-3">
                    <Badge variant={b.is_active ? "default" : "secondary"} className="text-[9px]"
                           style={b.is_active ? {background:'rgba(14,107,93,0.10)',color:'#0E6B5D'} : {}}>
                      {b.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-3 text-muted-foreground">{b.reference_count || 0}</td>
                  <td className="p-3 text-right space-x-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEdit(b)}><Pencil className="h-3 w-3" /></Button>
                    <span className="inline-flex items-center align-middle cursor-pointer" onClick={() => handleToggleActive(b)}>
                      <Switch checked={b.is_active} className="scale-75" />
                    </span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDelete(b)} disabled={b.reference_count > 0}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">Create Brand</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value, slug: slugify(e.target.value)})} className="mt-1" placeholder="Brand name" data-testid="brand-name-input" /></div>
            <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} className="mt-1 font-mono" placeholder="brand-slug" /></div>
            <div><Label className="text-xs">Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value)||0})} className="mt-1 w-20" /></div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="save-brand-btn">{saving ? 'Creating...' : 'Create Brand'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editBrand} onOpenChange={() => setEditBrand(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">Edit Brand</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1" /></div>
            <div><Label className="text-xs">Slug</Label><Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} className="mt-1 font-mono" /></div>
            <div><Label className="text-xs">Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value)||0})} className="mt-1 w-20" /></div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={handleUpdate} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
